import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Heart, Award, Users, Calendar, ChevronLeft, ChevronRight, LayoutGrid, List, RotateCcw } from 'lucide-react';
import LastSyncIndicator from '../../components/good-things/LastSyncIndicator';
import { GoodThingCard } from '../../components/good-things/GoodThingCard';
import { FavoriteButton } from '../../components/good-things/FavoriteButton';
import { goodThingsService, clearAllCaches } from '../../services/goodThings';
import { WeeklyStats, GoodThing } from '../../types/good-things';
import { Skeleton } from '../../components/ui/skeleton';
import { GoodThingDialog } from '../../components/good-things/GoodThingDialog';
import { GoodThingsGrid } from '../../components/good-things/GoodThingsGrid';
import { GoodThingsLine } from '../../components/good-things/GoodThingsLine';
import { LayoutToggle, LayoutType } from '../../components/good-things/LayoutToggle';

const WeeklyDigest = () => {
  usePageTitle('Good Thing Tracker');
  const [weeklyData, setWeeklyData] = useState<WeeklyStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [entries, setEntries] = useState<GoodThing[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [layoutType, setLayoutType] = useState<LayoutType>('grid');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const loadedIds = useRef(new Set<string>());
  const ITEMS_PER_PAGE = 5;

  const fetchWeeklyStats = async () => {
    setLoadError(null);
    try {
      setIsLoading(true);
      setEntries([]); // Clear entries when week changes
      setPage(1); // Reset page when week changes
      loadedIds.current.clear(); // Clear loaded IDs
      const data = await goodThingsService.getWeeklyStats(selectedWeek, 1, ITEMS_PER_PAGE);
      
      setWeeklyData(data);
      setEntries(data.featuredEntries);
      data.featuredEntries.forEach(entry => loadedIds.current.add(entry.id));
      setHasMore(data.hasMore);
    } catch (error) {
      
      setLoadError('Failed to load entries. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch initial data
  useEffect(() => {
    fetchWeeklyStats();
  }, [selectedWeek]);

  // Load more data when page changes
  useEffect(() => {
    const loadMore = async () => {
      if (page === 1) return; // Skip for initial load
      if (isLoadingMore) return; // Prevent multiple simultaneous loads
      
      try {
        setIsLoadingMore(true);
        const data = await goodThingsService.getWeeklyStats(selectedWeek, page, ITEMS_PER_PAGE);
        
        
        // Filter out any entries we've already loaded
        const newEntries = data.featuredEntries.filter(entry => !loadedIds.current.has(entry.id));
        if (newEntries.length > 0) {
          setEntries(prev => [...prev, ...newEntries]);
          newEntries.forEach(entry => loadedIds.current.add(entry.id));
        }

        // Check if we have more entries to load
        const totalLoaded = entries.length + newEntries.length;
        const hasMoreToLoad = totalLoaded < data.totalEntries;
        setHasMore(hasMoreToLoad);

        
      } catch (error) {
        
        setLoadError('Failed to load more entries. Please try again.');
      } finally {
        setIsLoadingMore(false);
      }
    };

    loadMore();
  }, [page, selectedWeek, entries.length]);

  // Infinite scroll
  const observer = useRef<IntersectionObserver>();
  const lastEntryRef = useCallback((node: HTMLDivElement) => {
    if (isLoading || isLoadingMore) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        
        setPage(prev => prev + 1);
      }
    });

    if (node) observer.current.observe(node);
  }, [isLoading, isLoadingMore, hasMore, page]);

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedWeek);
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() + 7);
    }
    setSelectedWeek(newDate);
  };

  const resetToCurrentWeek = () => {
    setSelectedWeek(new Date());
  };

  // Get the start of the current week (Monday)
  const getCurrentWeekStart = () => {
    const today = new Date();
    const currentWeekStart = new Date(today);
    currentWeekStart.setDate(today.getDate() - ((today.getDay() + 6) % 7)); // Go back to Monday
    currentWeekStart.setHours(0, 0, 0, 0);
    return currentWeekStart;
  };

  // Get the start of the selected week (Monday)
  const getSelectedWeekStart = () => {
    const selectedDate = new Date(selectedWeek);
    const selectedWeekStart = new Date(selectedDate);
    selectedWeekStart.setDate(selectedDate.getDate() - ((selectedDate.getDay() + 6) % 7)); // Go back to Monday
    selectedWeekStart.setHours(0, 0, 0, 0);
    return selectedWeekStart;
  };

  // Check if selected week is in the past
  const isPastWeek = () => {
    const currentWeekStart = getCurrentWeekStart();
    const selectedWeekStart = getSelectedWeekStart();
    return selectedWeekStart < currentWeekStart;
  };

  // Check if selected week is in the future
  const isFutureWeek = () => {
    const currentWeekStart = getCurrentWeekStart();
    const selectedWeekStart = getSelectedWeekStart();
    return selectedWeekStart > currentWeekStart;
  };

  // Check if selected week is the current week
  const isCurrentWeek = () => {
    const currentWeekStart = getCurrentWeekStart();
    const selectedWeekStart = getSelectedWeekStart();
    return selectedWeekStart.getTime() === currentWeekStart.getTime();
  };

  const formatWeekRange = (date: Date) => {
    const start = new Date(date);
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7)); // Go back to Monday
    const end = new Date(start);
    end.setDate(end.getDate() + 6);

    const formatDate = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  const formatName = (email: string): string => {
    return email
      .split('@')[0]
      .split('.')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const handleDialogSuccess = () => {
    setDialogOpen(false);
    clearAllCaches(); // Clear caches
    fetchWeeklyStats(); // Refresh data
  };

  const MetricCard = ({ icon: Icon, label, value, color }: { icon: any, label: string, value: string | number, color: string }) => {
    const iconColor = color.replace('bg-', 'text-').replace('-100', '-500');
    const darkIconColor = color.replace('bg-', 'text-').replace('-100', '-300');
    
    return (
      <Card className="relative overflow-hidden">
        <div className={`absolute top-0 right-0 w-20 h-20 transform translate-x-6 -translate-y-6`}>
          <div className={`absolute inset-0 ${color} dark:${color.replace('-100', '-900')} rounded-full opacity-20 dark:opacity-30`}></div>
        </div>
        <CardContent className="p-4 flex flex-col items-center justify-center text-center">
          <div className="flex items-center gap-2 mb-1">
            <Icon className={`h-4 w-4 ${iconColor} dark:${darkIconColor}`} />
            <h3 className="font-medium text-gray-600 dark:text-gray-400 text-xs">{label}</h3>
          </div>
          {isLoading ? (
            <Skeleton className="h-6 w-12" />
          ) : (
            <div className="text-xl font-bold text-gray-900 dark:text-white">{value}</div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-start mb-4 md:mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Weekly Digest</h1>
          <LastSyncIndicator />
          <div className="flex items-center gap-4 mt-2 md:mt-4">
            <button
              onClick={() => navigateWeek('prev')}
              className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <ChevronLeft className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
              <Calendar className="h-5 w-5" />
              <p>{formatWeekRange(selectedWeek)}</p>
              {isPastWeek() && (
                <button
                  onClick={resetToCurrentWeek}
                  className="ml-1 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded-full"
                  title="Return to current week"
                >
                  <RotateCcw className="h-3.5 w-3.5 text-gray-600 dark:text-gray-300" />
                </button>
              )}
            </div>
            <button
              onClick={() => navigateWeek('next')}
              className={`p-1 rounded-full ${
                isCurrentWeek() || isFutureWeek() 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
              disabled={isCurrentWeek() || isFutureWeek()}
            >
              <ChevronRight className={`h-5 w-5 ${
                isCurrentWeek() || isFutureWeek()
                  ? 'text-gray-300 dark:text-gray-600' 
                  : 'text-gray-500 dark:text-gray-400'
              }`} />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Only show layout toggle on desktop */}
          <div className="hidden md:block">
            <LayoutToggle 
              onLayoutChange={setLayoutType} 
              className="mr-2"
            />
          </div>
          <GoodThingDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            onSuccess={handleDialogSuccess}
          />
        </div>
      </div>

      {/* Weekly Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6 mb-6 md:mb-8">
        <MetricCard
          icon={Heart}
          label="Total Good Things"
          value={weeklyData?.totalEntries || 0}
          color="bg-pink-100"
        />
        <MetricCard
          icon={Award}
          label="Top Category"
          value={weeklyData?.trendingCategories[0]?.category || '-'}
          color="bg-purple-100"
        />
        <div className="col-span-2 md:col-span-1">
          <MetricCard
            icon={Users}
            label="Most Active"
            value={weeklyData?.topContributors[0]?.name || '-'}
            color="bg-blue-100"
          />
        </div>
      </div>

      {/* Good Things This Week */}
      <div>
        <div className="flex items-center gap-2 mb-3 md:mb-4">
          <Heart className="h-5 w-5 text-pink-500 dark:text-pink-300" />
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">Good Things This Week</h2>
          {entries.length > 0 && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              ({entries.length} of {weeklyData?.totalEntries})
            </span>
          )}
        </div>
        {layoutType === 'grid' ? (
          <GoodThingsGrid variant="compact" className="min-h-[400px]">
          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <Skeleton className="h-24 w-full" />
                </CardContent>
              </Card>
            ))
          ) : entries.length > 0 ? (
            entries.map((entry, index) => (
              <div
                key={`${entry.id}-${index}`}
                ref={index === entries.length - 1 ? lastEntryRef : undefined}
              >
                <GoodThingCard 
                  goodThing={entry} 
                  onEdit={fetchWeeklyStats}
                  onDelete={fetchWeeklyStats}
                />
              </div>
            ))
          ) : (
            <div className="text-center py-12 col-span-2">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-pink-100 dark:bg-pink-900 mb-4">
                <Heart className="h-8 w-8 text-pink-500 dark:text-pink-300" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No entries for this week</h3>
              <p className="text-gray-500 dark:text-gray-400">Check back later for new Good Things!</p>
            </div>
          )}
          {loadError && (
            <div className="col-span-2 text-center mt-4">
              <p className="text-red-500 dark:text-red-400 text-sm mb-2">{loadError}</p>
              <button
                onClick={() => setPage(prev => prev + 1)}
                className="text-blue-500 dark:text-blue-400 hover:underline"
              >
                Try Again
              </button>
            </div>
          )}
          {isLoadingMore && (
            <div className="col-span-2 flex justify-center mt-4">
              <Skeleton className="h-24 w-full max-w-md" />
            </div>
          )}
          {!hasMore && entries.length > 0 && (
            <div className="col-span-2 text-center mt-4 text-gray-500 dark:text-gray-400 text-xs md:text-sm italic">
              🌟 You've reached the end of this week's Good Things journey! Time to spread more positivity! ✨
            </div>
          )}
          </GoodThingsGrid>
        ) : (
          <GoodThingsLine variant="compact" className="min-h-[400px]">
          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <Skeleton className="h-24 w-full" />
                </CardContent>
              </Card>
            ))
          ) : entries.length > 0 ? (
            entries.map((entry, index) => (
              <div
                key={`${entry.id}-${index}`}
                ref={index === entries.length - 1 ? lastEntryRef : undefined}
              >
                <GoodThingCard 
                  goodThing={entry} 
                  onEdit={fetchWeeklyStats}
                  onDelete={fetchWeeklyStats}
                />
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-pink-100 dark:bg-pink-900 mb-4">
                <Heart className="h-8 w-8 text-pink-500 dark:text-pink-300" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No entries for this week</h3>
              <p className="text-gray-500 dark:text-gray-400">Check back later for new Good Things!</p>
            </div>
          )}
          {loadError && (
            <div className="text-center mt-4">
              <p className="text-red-500 dark:text-red-400 text-sm mb-2">{loadError}</p>
              <button
                onClick={() => setPage(prev => prev + 1)}
                className="text-blue-500 dark:text-blue-400 hover:underline"
              >
                Try Again
              </button>
            </div>
          )}
          {isLoadingMore && (
            <div className="flex justify-center mt-4">
              <Skeleton className="h-24 w-full max-w-md" />
            </div>
          )}
          {!hasMore && entries.length > 0 && (
            <div className="text-center mt-4 text-gray-500 dark:text-gray-400 text-xs md:text-sm italic">
              🌟 You've reached the end of this week's Good Things journey! Time to spread more positivity! ✨
            </div>
          )}
          </GoodThingsLine>
        )}
      </div>
    </div>
  );
};

export default WeeklyDigest;
